import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule, MatTabChangeEvent } from '@angular/material/tabs';
import { MatListModule } from '@angular/material/list';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { BreadcrumbsComponent } from '../../shared/breadcrumbs/breadcrumbs.component';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-diagnostics-detail',
  standalone: true,
  imports: [
    CommonModule,
    // UI
    MatCardModule,
    MatIconModule,
    MatTabsModule,
    MatListModule,
    MatButtonModule,
    MatChipsModule,
    MatTooltipModule,
    MatSnackBarModule,
    // Shared
    BreadcrumbsComponent
  ],
  templateUrl: './diagnostics-detail.component.html',
  styleUrls: ['./diagnostics-detail.component.scss']
})
export class DiagnosticsDetailComponent implements OnInit, OnDestroy {
  id: string | null = null;
  name: string | null = null;
  activeTab: 'data_general' | 'copia_cache' | 'info_metadata' | '' = 'data_general';

  /** Dato grezzo recuperato dallo storage */
  selectedAliasDocumentRow: Record<string, unknown> = {};

  /** Coppie key/value tipizzate a string per il template */
  aliasDocumentArrayData: Array<[string, string]> = [];

  /** Sezioni (valori sempre string) */
  dataGeneral: Array<[string, string]> = [];
  copiaCache: Array<[string, string]> = [];
  infoMetadata: Array<[string, string]> = [];

  /** Mappa per gestire l’espansione dei testi lunghi */
  private expanded: Record<string, boolean> = {};

  /** Subject per cleanup delle sottoscrizioni */
  private readonly destroy$ = new Subject<void>();

  labelMap: Record<string, string> = {
    url: 'URL',
    pageRank: 'PageRank',
    lang: 'Lingua',
    content: 'Contenuto',
    host: 'Host',
    canonical: 'Canonical'
  };

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.route.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params: ParamMap) => {
        this.id = params.get('id');
        const encodedName = params.get('name');
        if (encodedName) {
          try {
            this.name = decodeURIComponent(encodedName);
          } catch {
            this.name = encodedName;
          }
        } else {
          this.name = null;
        }
      });

    const stored = globalThis.localStorage?.getItem('selectedAliasDocumentRow');
    if (stored) {
      try {
        this.selectedAliasDocumentRow = JSON.parse(stored);
      } catch {
        this.selectedAliasDocumentRow = {};
      }

      // Normalizza tutti i valori a stringa per evitare errori nel template (es. uppercase pipe)
      this.aliasDocumentArrayData = Object.entries(this.selectedAliasDocumentRow)
        .map(([key, value]) => [key, this.coerceToString(value)] as [string, string]);

      // suddivisione campi
      this.dataGeneral = this.aliasDocumentArrayData.filter(([key]) =>
        ['url', 'pageRank', 'lang'].includes(key)
      );
      this.copiaCache = this.aliasDocumentArrayData.filter(([key]) => key === 'content');
      this.infoMetadata = this.aliasDocumentArrayData.filter(([key]) =>
        ['host', 'url', 'canonical'].includes(key)
      );
    }
  }

  ngOnDestroy(): void {
    // cleanup sottoscrizioni
    this.destroy$.next();
    this.destroy$.complete();

    // pulizia storage
    globalThis.localStorage?.removeItem('selectedAliasDocumentRow');
  }

  /** Converte un valore arbitrario in stringa sicura per il template */
  private coerceToString(value: unknown): string {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
      return String(value);
    }
    // Oggetti/array → JSON, evitando "[object Object]"
    try {
      return JSON.stringify(value);
    } catch {
      // ultimo fallback: tipo + toString (mai eccezioni qui)
      return Object.prototype.toString.call(value);
    }
  }

  getLabel(key: string): string {
    return this.labelMap[key] || key;
  }

  onTabChanged(event: MatTabChangeEvent): void {
    switch (event.index) {
      case 0:
        this.activeTab = 'data_general';
        break;
      case 1:
        this.activeTab = 'copia_cache';
        break;
      case 2:
        this.activeTab = 'info_metadata';
        break;
      default:
        this.activeTab = '';
    }
  }

  // --- UI helpers ---

  isUrlKey(key: string): boolean {
    return key === 'url' || key === 'canonical';
  }

  openInNewTab(url: string): void {
    try {
      // Preferire globalThis a window per portabilità/SSR
      globalThis.open?.(url, '_blank', 'noopener,noreferrer');
    } catch {
      /* no-op */
    }
  }

  async copyToClipboard(text: string): Promise<void> {
    try {
      const nav = globalThis.navigator as Navigator | undefined;
      if (nav?.clipboard?.writeText) {
        await nav.clipboard.writeText(text);
      } else {
        // Fallback manuale
        globalThis.prompt?.('Copia il testo qui sotto e premi ⌘/Ctrl+C', text);
      }
      this.snackBar.open('Copiato negli appunti', 'Chiudi', {
        duration: 2500,
        panelClass: ['snackbar-success']
      });
    } catch {
      globalThis.prompt?.('Copia il testo qui sotto e premi ⌘/Ctrl+C', text);
      this.snackBar.open('Testo pronto per la copia manuale', 'Chiudi', {
        duration: 3500
      });
    }
  }

  toggleExpand(key: string): void {
    this.expanded[key] = !this.expanded[key];
  }

  isExpanded(key: string): boolean {
    return !!this.expanded[key];
  }
}